import { useAuth } from '../../contexts/AuthContext';
import WorkerHealth from "./WorkerHealth";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  assistantService,
  agentService,
  mailService,
  documentService,
} from "../../services/api";
import AgentDesk from "./AgentDesk";
import DocumentActionReview from "./DocumentActionReview";
import NotificationDesk, { DeliverySettings } from "./NotificationDesk";
import PeopleDesk from "./PeopleDesk";
import FinanceDesk from "./FinanceDesk";
import VehicleDesk from "./VehicleDesk";
import RoutineDesk from "./RoutineDesk";
import { TrackerSources } from "./TrackerPage";
import { MailSources } from "./MailPage";

const domains = [
  "work",
  "family",
  "health",
  "finance",
  "maintenance",
  "travel",
  "learning",
  "leisure",
];
const views = [
  "Attention",
  "Notifications",
  "Trackers",
  "Mail",
  "Agent",
  "People",
  "Finance",
  "Vehicles",
  "Routines",
  "Responsibilities",
  "Connections",
  "Outbox",
  "Settings",
];
const kinds = [
  "task",
  "bug",
  "meeting",
  "follow_up",
  "birthday",
  "check_in",
  "routine",
  "reconcile",
  "service",
  "trip",
  "course",
  "discovery",
  "commute",
];
const label = (value) => value.replaceAll("_", " ");
const inputClass =
  "mt-1 block w-full rounded-md border border-[#dce3dd] bg-white p-2 text-sm text-[#17211c]";
const emptyItem = {
  title: "",
  description: "",
  domain: "work",
  kind: "task",
  owner: "",
  priority: "normal",
  status: "open",
  due_at: "",
  recurrence: "none",
  lead_minutes: 60,
  source_url: "",
  details: {},
};
const templates = [
  ["Work deadline", "work", "task", "none", 60],
  ["Bug escalation", "work", "bug", "none", 60],
  ["Meeting follow-up", "work", "follow_up", "none", 0],
  ["Family check-in", "family", "check_in", "weekly", 60],
  ["Birthday & gift preparation", "family", "birthday", "yearly", 10080],
  ["Training or meal routine", "health", "routine", "daily", 60],
  ["Balance reconciliation", "finance", "reconcile", "monthly", 1440],
  ["Subscription renewal", "finance", "task", "monthly", 10080],
  ["Vehicle service", "maintenance", "service", "none", 1440],
  ["Trip preparation", "travel", "trip", "none", 20160],
  ["Courses & events", "learning", "course", "weekly", 60],
  ["Commute podcast", "leisure", "commute", "daily", 60],
  ["Weekend plans", "leisure", "discovery", "weekly", 1440],
];
const localInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};
const displayTime = (value, timezone) =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: timezone,
      }).format(new Date(value))
    : "Needs a date";
const safeLink = (value) => /^https?:\/\//i.test(value || "");

function Field({ title, children }) {
  return (
    <label className="block text-xs font-semibold text-[#58635d]">
      {title}
      {children}
    </label>
  );
}
function Select({ title, value, options, onChange }) {
  return (
    <Field title={title}>
      <select
        className={inputClass}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {label(option)}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function ResponsibilityForm({
  initial = emptyItem,
  onSave,
  onClose,
  busy,
}) {
  const [item, setItem] = useState({
    ...initial,
    due_at: localInput(initial.due_at),
  });
  const set = (key, value) =>
    setItem((previous) => ({ ...previous, [key]: value }));
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        const {
          id,
          attention,
          reason,
          source_key,
          snoozed_until,
          completed_at,
          _agentProposalId,
          _proposalIndex,
          ...values
        } = item;
        onSave({
          ...values,
          due_at: values.due_at ? new Date(values.due_at).toISOString() : null,
        });
      }}
    >
      <Field title="Responsibility">
        <input
          autoFocus
          required
          maxLength={240}
          className={inputClass}
          value={item.title}
          onChange={(event) => set("title", event.target.value)}
        />
      </Field>
      <Field title="Context and next step">
        <textarea
          rows={3}
          className={inputClass}
          value={item.description}
          onChange={(event) => set("description", event.target.value)}
        />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          title="Life area"
          value={item.domain}
          options={domains}
          onChange={(value) => set("domain", value)}
        />
        <Select
          title="Type"
          value={item.kind}
          options={kinds}
          onChange={(value) => set("kind", value)}
        />
        <Field title="Owner / person to follow up with">
          <input
            className={inputClass}
            value={item.owner}
            onChange={(event) => set("owner", event.target.value)}
          />
        </Field>
        <Select
          title="Priority"
          value={item.priority}
          options={["critical", "high", "normal", "low"]}
          onChange={(value) => set("priority", value)}
        />
        <Field
          title={`Due / next check-in (${Intl.DateTimeFormat().resolvedOptions().timeZone})`}
        >
          <input
            type="datetime-local"
            required={item.recurrence !== "none"}
            className={inputClass}
            value={item.due_at}
            onChange={(event) => set("due_at", event.target.value)}
          />
        </Field>
        <Select
          title="Repeat after completion"
          value={item.recurrence}
          options={["none", "daily", "weekly", "monthly", "yearly"]}
          onChange={(value) => set("recurrence", value)}
        />
        <Field title="Remind before (minutes)">
          <input
            type="number"
            min={0}
            max={129600}
            required
            className={inputClass}
            value={item.lead_minutes}
            onChange={(event) =>
              set("lead_minutes", Number(event.target.value))
            }
          />
        </Field>
        <Select
          title="Status"
          value={item.status}
          options={["open", "waiting", "blocked", "done", "cancelled"]}
          onChange={(value) => set("status", value)}
        />
      </div>
      {item.kind === "service" && (
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            ["current_reading", "Current odometer reading"],
            ["service_at_reading", "Service due at reading"],
          ].map(([key, title]) => (
            <Field key={key} title={title}>
              <input
                type="number"
                min={0}
                className={inputClass}
                value={item.details?.[key] ?? ""}
                onChange={(event) =>
                  set("details", {
                    ...item.details,
                    [key]: Number(event.target.value),
                  })
                }
              />
            </Field>
          ))}
        </div>
      )}
      <Field title="Source link (optional)">
        <input
          type="url"
          className={inputClass}
          value={item.source_url}
          onChange={(event) => set("source_url", event.target.value)}
        />
      </Field>
      <div className="flex gap-2">
        <button disabled={busy} className="primary-button">
          {busy ? "Saving…" : "Save responsibility"}
        </button>
        <button type="button" className="secondary-button" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ResponsibilityCard({
  item,
  timezone,
  run,
  edit,
  draft,
  busy,
  openAgent,
  openPeople,
  openFinance,
  openVehicles,
  openRoutines,
}) {
  const tracker = item.source_key?.startsWith("sheet:");
  const managedGoogleTask = item.details?.managed_by === "google_tasks";
  const managedPerson = item.details?.managed_by === "people";
  const managedFinance = item.details?.managed_by === "finance";
  const managedVehicle = item.details?.managed_by === "vehicles";
  const managedRoutine = item.details?.managed_by === "routines";
  const managedPlan = item.details?.managed_by === "plans";
  const managedChallenge = item.details?.managed_by === "challenges";
  const managedMail = item.details?.managed_by === "mail";
  const managedMeeting = item.details?.managed_by === "meetings" || item.details?.calendar_response;
  return (
    <article className="surface-panel p-5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span
          className={`status-pill ${["critical", "high"].includes(item.priority) ? "status-pill-warning" : "status-pill-info"}`}
        >
          {item.priority}
        </span>
        <span className="capitalize text-[#758078]">
          {item.domain} · {label(item.kind)} · {item.status}
        </span>
      </div>
      <h3 className="mt-3 text-base font-bold">{item.title}</h3>
      {managedChallenge && <Link className="quiet-button mt-2 text-xs" to="/challenges">Open challenges and tick completed days</Link>}
      {tracker && item.details?.source_id && (
        <Link
          className="quiet-button mt-2 text-xs"
          to={`/trackers/${item.details.source_id}`}
        >
          Open work tracker
        </Link>
      )}
      {managedGoogleTask && <Link className="quiet-button mt-2 text-xs" to={item.details.note_id ? `/notes?note=${item.details.note_id}` : '/integrations'}>{item.details.note_id ? 'Review linked Google Task' : 'Open Google Task connections'}</Link>}
      {managedGoogleTask && item.details.note_id && <p className="mt-2 text-xs text-[#68746d]">Google state: {item.details.availability}. Last checked: {item.details.last_checked_at ? displayTime(item.details.last_checked_at, timezone) : 'Not confirmed'}. Status changes use Google Task review.</p>}
      {managedPlan && (
        <Link
          className="quiet-button text-xs mt-2"
          to={`/plans/${item.details.plan_id}`}
        >
          Open plan and record outcome
        </Link>
      )}
      {item.details?.mail_thread_id && (
        <Link
          className="quiet-button mt-2 text-xs"
          to={`/mail/threads/${item.details.mail_thread_id}`}
        >
          Open email conversation
        </Link>
      )}
      {item.details?.event_id && (
        <Link
          className="quiet-button mt-2 text-xs"
          to={item.details.calendar_response ? `/calendar/events/${item.details.event_id}` : `/meetings/${item.details.event_id}`}
        >
          {item.details.calendar_response ? "Review invitation response" : "Open meeting notes and actions"}
        </Link>
      )}
      {managedPerson && (
        <button className="quiet-button mt-2 text-xs" onClick={openPeople}>
          Open people and occasions
        </button>
      )}
      {managedFinance && (
        <button className="quiet-button mt-2 text-xs" onClick={openFinance}>
          Open financial records
        </button>
      )}
      {managedVehicle && (
        <button className="quiet-button mt-2 text-xs" onClick={openVehicles}>
          Open vehicle maintenance
        </button>
      )}
      {managedRoutine && (
        <button className="quiet-button mt-2 text-xs" onClick={openRoutines}>
          Open routine and activity
        </button>
      )}
      {(item.details?.discovery_brief_id || item.details?.discovery_watch_id || item.details?.agent_review) && (
        item.details?.discovery_brief_id ? <Link className="quiet-button mt-2 text-xs" to={`/assistant?view=agent&brief=${item.details.discovery_brief_id}`}>Open research briefing</Link> : <button className="quiet-button mt-2 text-xs" onClick={openAgent}>
          Open agent work
        </button>
      )}
      {item.description && (
        <p className="mt-2 whitespace-pre-wrap text-sm text-[#66716a]">
          {item.description}
        </p>
      )}
      <p className="mt-3 text-xs font-semibold">
        {managedGoogleTask ? (item.details.scheduled_date ? `Google scheduled day: ${item.details.scheduled_date}` : "No Google scheduled day") : managedVehicle && !item.due_at
          ? "Based on recorded service and odometer history"
          : displayTime(item.due_at, timezone)}
        {item.owner ? ` · ${item.owner}` : ""}
        {item.recurrence !== "none" ? ` · ${item.recurrence}` : ""}
      </p>
      {item.reason && (
        <p className="mt-2 text-sm text-[#996617]">
          {item.attention === "overdue"
            ? "Overdue. Review the next step and follow up with the owner."
            : item.reason}
        </p>
      )}
      {item.details?.missing_from_latest_sync && (
        <p className="mt-2 text-xs text-[#a14d38]">
          Missing from the latest sheet range. Check the source before closing.
        </p>
      )}
      {item.snoozed_until && (
        <p className="mt-2 text-xs text-[#758078]">
          Snoozed until {displayTime(item.snoozed_until, timezone)}
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        {!tracker &&
          !managedGoogleTask &&
          !managedPerson &&
          !managedFinance &&
          !managedVehicle &&
          !managedRoutine &&
          !managedMail &&
          !managedPlan &&
          !managedChallenge &&
          !managedMeeting &&
          !["done", "cancelled"].includes(item.status) && (
            <button
              disabled={busy}
              className="secondary-button min-h-8 text-xs"
              onClick={() =>
                run(() =>
                  assistantService.state(item.id, {
                    action: "complete",
                    expected_due_at: item.due_at,
                  }),
                )
              }
            >
              Complete{item.recurrence !== "none" ? " & advance" : ""}
            </button>
          )}
        {!["done", "cancelled"].includes(item.status) && (
          <button
            disabled={busy}
            className="quiet-button min-h-8 text-xs"
            onClick={() =>
              run(() =>
                assistantService.state(item.id, {
                  action: "snooze",
                  until: new Date(Date.now() + 3600000).toISOString(),
                }),
              )
            }
          >
            Snooze 1 hour
          </button>
        )}
        {(!item.source_key || item.source_key.startsWith("agent:")) && (
          <button
            className="quiet-button min-h-8 text-xs"
            onClick={() => edit(item)}
          >
            Edit
          </button>
        )}
        {["done", "cancelled"].includes(item.status) &&
          !tracker &&
          !managedGoogleTask &&
          !managedPerson &&
          !managedFinance &&
          !managedVehicle &&
          !managedRoutine &&
          !managedMail &&
          !managedPlan &&
          !managedChallenge &&
          !managedMeeting && (
            <button
              disabled={busy}
              className="quiet-button min-h-8 text-xs"
              onClick={() =>
                run(() => assistantService.state(item.id, { action: "reopen" }))
              }
            >
              Reopen
            </button>
          )}
        <button
          className="quiet-button min-h-8 text-xs"
          onClick={() => draft(item)}
        >
          Draft follow-up
        </button>
        {safeLink(item.source_url) && (
          <a
            className="quiet-button min-h-8 text-xs"
            href={item.source_url}
            target="_blank"
            rel="noreferrer"
          >
            Open source ↗
          </a>
        )}
      </div>
    </article>
  );
}

function Connections({ connections, sources, run, busy, extractSource }) {
  const [provider, setProvider] = useState("sheets");
  const [title, setTitle] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [range, setRange] = useState("A1:Z1000");
  const [kind, setKind] = useState("task");
  const [sourceDomain, setSourceDomain] = useState("work");
  const [analyseMail, setAnalyseMail] = useState(false);
  const [columns, setColumns] = useState({
    id: "id",
    title: "title",
    owner: "owner",
    status: "status",
    priority: "priority",
    due: "due",
  });
  const [tokenProvider, setTokenProvider] = useState("notion");
  const [token, setToken] = useState("");
  return (
    <div className="space-y-5">
      <section className="surface-panel flex flex-wrap items-center justify-between gap-3 p-5">
        <div>
          <h2 className="font-bold">Document sources</h2>
          <p className="mt-1 text-sm text-[#66716a]">
            Find Google Docs and shared Notion pages, review their content and
            prepare source-linked follow-ups.
          </p>
        </div>
        <Link className="primary-button" to="/documents">
          Browse documents
        </Link>
      </section>
      <section className="surface-panel flex flex-wrap items-center justify-between gap-3 p-5">
        <div><h2 className="font-bold">Public X sources</h2><p className="mt-1 text-sm text-[#66716a]">Search announcements and monitor topics with your connected X API access.</p></div>
        <Link className="primary-button" to="/social">Browse X sources</Link>
      </section>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {connections.map((connection) => (
          <div className="surface-panel p-4" key={connection.id}>
            <h3 className="font-bold">{connection.name}</h3>
            <p className="mt-2 text-xs leading-5 text-[#66716a]">
              {connection.description}
            </p>
            <span className="status-pill mt-3">{label(connection.status)}</span>
            {(connection.can_disconnect || connection.status === "token_saved") && (
              <button
                className="quiet-button mt-2 text-xs"
                disabled={busy}
                onClick={() =>
                  run(() => assistantService.disconnect(connection.id))
                }
              >
                Disconnect
              </button>
            )}
          </div>
        ))}
      </div>
      <Link to="/integrations" className="secondary-button">
        Connect / reconnect Google
      </Link>
      <p className="text-xs text-[#66716a]">
        Google permissions are checked per service. Notion pages must be shared
        with your integration. X requires API access. Other notes and socials
        can be pasted into “Extract from notes”. Open a plan to research places and routes.
      </p>
      <form
        className="surface-panel space-y-3 p-5"
        onSubmit={(event) => {
          event.preventDefault();
          run(async () => {
            await assistantService.connect({ provider: tokenProvider, token });
            setToken("");
          });
        }}
      >
        <h2 className="font-bold">Connect Notion or X</h2>
        <div className="grid gap-3 sm:grid-cols-[160px_1fr_auto]">
          <Select
            title="Service"
            value={tokenProvider}
            options={["notion", "x"]}
            onChange={setTokenProvider}
          />
          <Field title="Integration / API bearer token">
            <input
              type="password"
              autoComplete="off"
              required
              minLength={10}
              className={inputClass}
              value={token}
              onChange={(event) => setToken(event.target.value)}
            />
          </Field>
          <button disabled={busy} className="secondary-button self-end">
            Save connection
          </button>
        </div>
      </form>
      <form
        className="surface-panel space-y-4 p-5"
        onSubmit={(event) => {
          event.preventDefault();
          run(async () => {
            await assistantService.addSource({
              provider,
              title,
              external_id: identifier,
              config:
                provider === "sheets"
                  ? { range, columns, kind, domain: sourceDomain }
                  : { domain: sourceDomain, ...(provider === "gmail" ? {analyse_conversations: analyseMail} : {}) },
            });
            setTitle("");
            setIdentifier("");
          });
        }}
      >
        <h2 className="font-bold">Choose what Timelyzer monitors</h2>
        <Select
          title="Source life area"
          value={sourceDomain}
          options={domains}
          onChange={setSourceDomain}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            title="Source"
            value={provider}
            options={[
              "calendar",
              "gmail",
              "drive",
              "docs",
              "sheets",
              "notion",
              "x",
            ]}
            onChange={setProvider}
          />
          <Field title="Name">
            <input
              required
              className={inputClass}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>
        </div>
        <Field
          title={
            provider === "gmail"
              ? "Gmail search (for example: is:unread newer_than:7d)"
              : provider === "calendar"
                ? "Calendar ID (primary for your main calendar)"
                : provider === "drive"
                  ? "Enter recent for the latest 100 files"
                  : provider === "x" ? "X post URL or numeric ID" : "Document, sheet or page ID from the URL"
          }
        >
          <input
            required
            className={inputClass}
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
          />
        </Field>
        {provider === "gmail" && <div className="space-y-2">
          <label className="flex gap-2 items-center text-sm"><input type="checkbox" checked={analyseMail} onChange={event => setAnalyseMail(event.target.checked)} />Analyse conversations for responsibilities</label>
          <p className="text-xs text-[#66716a]">Reads and saves up to five full conversations per sync, continuing through this search. Agent analysis checks incoming and outgoing context for outstanding commitments. Background checks need monitoring and automatic extraction enabled. Your existing review or automatic local-task setting applies; no email is sent.</p>
        </div>}
        {provider === "sheets" && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field title="Sheet range (include the header row)">
                <input
                  className={inputClass}
                  value={range}
                  onChange={(event) => setRange(event.target.value)}
                />
              </Field>
              <Select
                title="Tracker type"
                value={kind}
                options={["task", "bug"]}
                onChange={setKind}
              />
            </div>
            <p className="text-xs text-[#66716a]">
              Match your sheet’s column headers below. IDs must be stable and
              unique. Dates use YYYY-MM-DD or ISO timestamps.
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              {Object.entries(columns).map(([key, value]) => (
                <Field key={key} title={`${label(key)} column`}>
                  <input
                    required={["id", "title"].includes(key)}
                    className={inputClass}
                    value={value}
                    onChange={(event) =>
                      setColumns({ ...columns, [key]: event.target.value })
                    }
                  />
                </Field>
              ))}
            </div>
          </>
        )}
        <button disabled={busy} className="primary-button">
          Add source
        </button>
      </form>
      {sources.map((source) => (
        <article className="surface-panel p-5" key={source.id}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-bold">{source.title}</h3>
            <span className="status-pill">{source.provider === "drive_file" ? "Google Drive file" : source.provider}</span>
          </div>
          {["docs", "notion", "drive_file"].includes(source.provider) && (
            <Link
              className="quiet-button mt-2 text-xs"
              to={`/documents/${source.id}`}
            >
              Open document workspace
            </Link>
          )}
          {source.provider === "x" && <Link className="quiet-button mt-2 text-xs" to="/social">Open X source workspace</Link>}
          {source.provider === "gmail" && (
            <Link className="quiet-button mt-2 text-xs" to="/mail">
              Open conversations and follow-ups
            </Link>
          )}
          {source.provider === "gmail" && !source.config?.mailbox_browser && <div className="mt-3 space-y-2">
            <label className="flex gap-2 items-center text-sm"><input type="checkbox" disabled={busy} checked={source.config?.analyse_conversations === true} onChange={event => run(() => assistantService.mailAnalysis(source.id, event.target.checked))} />Analyse conversations in {source.title}</label>
            {source.config?.analyse_conversations && <p className="text-xs">Conversation analysis enabled. Sync now to read a page, then use Agent → Process new information; enabled background workers do this automatically. Only complete text checked within 30 minutes can produce or apply proposals.</p>}
            {source.preview.triage && <p className="text-xs">Last page: {source.preview.triage.checked} conversations checked. {source.preview.triage.more_pages ? "More pages will be checked on the next sync." : "Search cycle finished; the next sync starts with newest results."}{source.preview.triage.restarted && " An expired page was restarted."}</p>}
            {source.preview.triage?.skipped?.map(item => <p className="text-xs text-amber-800" key={item.thread_id}>{item.subject}: {item.reason} <Link className="underline" to={`/mail/threads/${item.thread_id}`}>Review conversation</Link></p>)}
          </div>}
          {source.config?.mailbox_browser && <p className="mt-3 text-xs">Opened conversations only. Manage explicit follow-ups in Mail; this source does not scan the inbox.</p>}
          {source.provider === "sheets" && (
            <Link
              className="quiet-button mt-2 text-xs"
              to={`/trackers/${source.id}`}
            >
              Open work tracker
            </Link>
          )}
          <p className="mt-2 text-xs text-[#66716a]">
            {source.synced_at
              ? `Last synced ${new Date(source.synced_at).toLocaleString()}`
              : "Not synced yet"}
            {source.preview.limited ? " · Preview limit reached" : ""}
          </p>
          {source.error && (
            <p role="alert" className="mt-3 text-sm text-[#a14d38]">
              {source.error}
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {!source.config?.mailbox_browser && <button
              disabled={busy}
              className="secondary-button"
              onClick={() => run(() => assistantService.sync(source.id))}
            >
              Sync now
            </button>}
            {source.preview.text &&
              !["calendar", "sheets", "gmail", "x"].includes(source.provider) && (
                <button
                  className="secondary-button"
                  onClick={() => extractSource(source.preview.text)}
                >
                  Extract responsibilities
                </button>
              )}
            <button
              disabled={busy}
              className="quiet-button"
              onClick={() =>
                run(() => assistantService.removeSource(source.id))
              }
            >
              Stop monitoring
            </button>
          </div>
          {source.preview.text && (
            <details className="mt-4">
              <summary className="cursor-pointer text-xs font-semibold">
                Source preview
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words text-xs text-[#66716a]">
                {source.preview.text}
              </pre>
            </details>
          )}
        </article>
      ))}
    </div>
  );
}

function ActionForm({ initial, run, busy, onSaved }) {
  const [kind, setKind] = useState("email");
  const [subject, setSubject] = useState(initial?.title || "");
  const [body, setBody] = useState(
    initial
      ? `Following up on: ${initial.title}\n\nPlease share the current status, blockers and next step.`
      : "",
  );
  const [recipients, setRecipients] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [target, setTarget] = useState("");
  return (
    <form
      className="surface-panel space-y-4 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        run(async () => {
          await assistantService.draft({
            kind,
            subject,
            body,
            recipients: recipients
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean),
            start: start ? new Date(start).toISOString() : null,
            end: end ? new Date(end).toISOString() : null,
            target_id: target,
          });
          setSubject("");
          setBody("");
          onSaved();
        });
      }}
    >
      <h2 className="font-bold">Prepare an action</h2>
      <Select
        title="Action"
        value={kind}
        options={["email", "calendar", "docs_append", "notion_append"]}
        onChange={setKind}
      />
      <Field title="Subject / title">
        <input
          required
          className={inputClass}
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
        />
      </Field>
      {["email", "calendar"].includes(kind) ? (
        <Field title="Recipients (comma-separated email addresses)">
          <input
            required
            className={inputClass}
            value={recipients}
            onChange={(event) => setRecipients(event.target.value)}
          />
        </Field>
      ) : (
        <Field title="Destination document / page ID">
          <input
            required
            className={inputClass}
            value={target}
            onChange={(event) => setTarget(event.target.value)}
          />
        </Field>
      )}
      <Field title="Message / notes to append">
        <textarea
          required
          rows={5}
          className={inputClass}
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
      </Field>
      {kind === "calendar" && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field title="Start (device timezone)">
            <input
              required
              type="datetime-local"
              className={inputClass}
              value={start}
              onChange={(event) => setStart(event.target.value)}
            />
          </Field>
          <Field title="End (device timezone)">
            <input
              required
              type="datetime-local"
              className={inputClass}
              value={end}
              onChange={(event) => setEnd(event.target.value)}
            />
          </Field>
        </div>
      )}
      <p className="text-xs text-[#66716a]">
        Save a draft, review the exact content below, then send. Calendar
        actions send invitations to the listed attendees. Note actions append
        text to the selected document.
      </p>
      <button disabled={busy} className="primary-button">
        Save draft for review
      </button>
    </form>
  );
}

function Settings({ preferences, runtime = {}, run, busy }) {
  const { refreshUser } = useAuth();
  const [values, setValues] = useState(preferences);
  const set = (key, value) => setValues({ ...values, [key]: value });
  return (
    <form
      className="surface-panel max-w-2xl space-y-5 p-6"
      onSubmit={(event) => {
        event.preventDefault();
        run(async () => {
          await assistantService.preferences(values);
          await refreshUser();
          setValues((current) => ({ ...current, gmail_selection: undefined }));
        });
      }}
    >
      <h2 className="text-lg font-bold">When Timelyzer should check in</h2>
      <label className="flex gap-3 text-sm">
        <input
          type="checkbox"
          checked={!!values.automatic_extraction}
          onChange={(event) =>
            set("automatic_extraction", event.target.checked)
          }
        />
        Analyse new notes and connected information automatically
      </label>
      <label className="flex gap-3 text-sm">
        <input
          type="checkbox"
          checked={!!values.auto_create_responsibilities}
          onChange={(event) =>
            set("auto_create_responsibilities", event.target.checked)
          }
        />
        Automatically track clear actions supported by source evidence
      </label>
      <p className="text-xs leading-5 text-[#66716a]">
        Uncertain actions stay in the Agent review queue. These settings create
        local responsibilities; emails and invitations still use the outbox.
      </p>
      <Field title="Daily agent AI calls">
        <input
          type="number"
          min={1}
          max={50}
          required
          className={inputClass}
          value={values.daily_ai_limit ?? 12}
          onChange={(event) =>
            set("daily_ai_limit", Number(event.target.value))
          }
        />
      </Field>
      <WorkerHealth report={runtime.workers} timezone={preferences.timezone} monitoring={preferences.enabled} />
      {runtime.email_configured === false && (
        <p className="text-sm text-[#996617]">
          Email delivery is not configured. Reminders are available in the app.
        </p>
      )}
      {preferences.last_checked_at && (
        <p className="text-xs text-[#66716a]">
          Last background check:{" "}
          {displayTime(preferences.last_checked_at, preferences.timezone)}
        </p>
      )}
      <label className="flex gap-3 text-sm">
        <input
          type="checkbox"
          checked={values.enabled}
          onChange={(event) => set("enabled", event.target.checked)}
        />
        Enable background checks and source sync
      </label>
      <p className="text-xs leading-5 text-[#66716a]">
        Checks run every minute and sources refresh every 15 minutes while the
        backend is running. In-app reminders persist when you close the browser.
      </p>
      <Field title="Timezone">
        <input
          required
          className={inputClass}
          value={values.timezone}
          onChange={(event) => set("timezone", event.target.value)}
        />
      </Field>
      <button
        type="button"
        className="quiet-button text-xs"
        onClick={() =>
          set("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone)
        }
      >
        Use my device timezone
      </button>
      <div className="grid gap-3 sm:grid-cols-2">
        {[
          ["quiet_start", "Quiet hours start (0–23)"],
          ["quiet_end", "Quiet hours end (0–23)"],
        ].map(([key, title]) => (
          <Field key={key} title={title}>
            <input
              type="number"
              min={0}
              max={23}
              required
              className={inputClass}
              value={values[key]}
              onChange={(event) => set(key, Number(event.target.value))}
            />
          </Field>
        ))}
      </div>
      <p className="text-xs text-[#66716a]">
        Equal start and end hours disable quiet hours. Use reminder email
        preferences below to choose whether critical emails can bypass them.
      </p>
      <fieldset>
        <legend className="mb-2 text-xs font-semibold">Weekend days</legend>
        <div className="flex flex-wrap gap-3">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
            (day, index) => (
              <label key={day} className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={values.weekend_days.includes(index)}
                  onChange={(event) =>
                    set(
                      "weekend_days",
                      event.target.checked
                        ? [...values.weekend_days, index]
                        : values.weekend_days.filter(
                            (value) => value !== index,
                          ),
                    )
                  }
                />
                {day}
              </label>
            ),
          )}
        </div>
      </fieldset>
      <label className="flex gap-3 text-sm">
        <input
          type="checkbox"
          checked={values.email_notifications}
          onChange={(event) => set("email_notifications", event.target.checked)}
        />
        Email new reminders to my account address
      </label>
      <DeliverySettings
        value={values.notification_delivery}
        onChange={(value) => set("notification_delivery", value)}
        runtime={runtime}
        disabled={busy}
        onGmailSelection={(selection) => setValues((current) => ({ ...current, gmail_selection: selection }))}
      />
      <button disabled={busy} className="primary-button">
        Save preferences
      </button>
    </form>
  );
}

export default function AssistantPage() {
  const client = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["assistant"],
    queryFn: assistantService.briefing,
    refetchInterval: 60000,
  });
  const { data: connections = [] } = useQuery({
    queryKey: ["assistant-connections"],
    queryFn: assistantService.connections,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const receiptKey = searchParams.get("receipt");
  const actionKey = searchParams.get("action");
  const validActionKey = /^[1-9]\d*$/.test(actionKey || "") && Number.isSafeInteger(Number(actionKey));
  const selectedAction = useQuery({
    queryKey: ["assistant-action", actionKey],
    queryFn: () => assistantService.action(Number(actionKey)),
    enabled: validActionKey,
    retry: false,
    refetchInterval: 15000,
  });
  const receiptAction = useQuery({
    queryKey: ["document-receipt", receiptKey],
    queryFn: () => documentService.receipt(receiptKey),
    enabled: !!receiptKey,
  });
  const tab =
    views.find((value) => value.toLowerCase() === searchParams.get("view")) ||
    "Attention";
  const setTab = (value) =>
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value === "Attention") next.delete("view");
      else next.set("view", value.toLowerCase());
      return next;
    });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [content, setContent] = useState("");
  const [extraction, setExtraction] = useState(null);
  const [extractOpen, setExtractOpen] = useState(false);
  const [draftItem, setDraftItem] = useState(null);
  const [domain, setDomain] = useState("all");
  const [showClosed, setShowClosed] = useState(false);
  const [research, setResearch] = useState("");
  const [location, setLocation] = useState("");
  const [links, setLinks] = useState(null);
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["assistant"] }),
      client.invalidateQueries({ queryKey: ["assistant-connections"] }),
      client.invalidateQueries({ queryKey: ["today"] }),
      client.invalidateQueries({ queryKey: ["calendar-events"] }),
      client.invalidateQueries({ queryKey: ["preference-profile"] }),
      client.invalidateQueries({ queryKey: ["agent"] }),
      client.invalidateQueries({ queryKey: ["record-proposals"] }),
      client.invalidateQueries({ queryKey: ["people"] }),
      client.invalidateQueries({ queryKey: ["contacts"] }),
      client.invalidateQueries({ queryKey: ["finance"] }),
      client.invalidateQueries({ queryKey: ["ledger-transactions"] }),
      client.invalidateQueries({ queryKey: ["vehicles"] }),
      client.invalidateQueries({ queryKey: ["vehicle-history"] }),
      client.invalidateQueries({ queryKey: ["routines"] }),
      client.invalidateQueries({ queryKey: ["routine-schedule"] }),
      client.invalidateQueries({ queryKey: ["routine-history"] }),
      client.invalidateQueries({ queryKey: ["routine-research"] }),
      client.invalidateQueries({ queryKey: ["meeting"] }),
      client.invalidateQueries({ queryKey: ["document-receipt"] }),
      client.invalidateQueries({ queryKey: ["assistant-action"] }),
      client.invalidateQueries({ queryKey: ["tracker"] }),
      client.invalidateQueries({ queryKey: ["documents"] }),
    ]);
  };
  const run = async (operation) => {
    if (busy) return;
    setBusy(true);
    try {
      await operation();
      await refresh();
    } catch (failure) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Assistant action failed", failure.message);
      }
      const detail = failure.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((item) => item.msg).join("; ")
            : "Could not complete the action. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };
  if (isLoading)
    return (
      <div className="app-page">
        <p role="status">Loading your assistant…</p>
      </div>
    );
  if (error)
    return (
      <div className="app-page">
        <p role="alert">Your assistant could not load.</p>
        <button className="secondary-button mt-4" onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );
  const items = (
    tab === "Attention" ? data.attention : data.responsibilities
  ).filter(
    (item) =>
      (domain === "all" || item.domain === domain) &&
      (showClosed || !["done", "cancelled"].includes(item.status)),
  );
  return (
    <div className="app-page">
      <div className="app-page-inner">
        <header className="page-header">
          <div>
            <div className="eyebrow">
              {data.is_weekend ? "Weekend" : "Today"} · {data.period} ·{" "}
              {data.timezone}
            </div>
            <h1 className="page-title">{tab === "Attention" ? "Needs attention" : tab === "Agent" ? "Research & schedules" : tab === "Outbox" ? "Drafts & approvals" : tab}</h1>
            <p className="page-description">
              Keep promises, close loops, and make room for life outside work.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              className="secondary-button"
              onClick={() => setExtractOpen(true)}
            >
              Extract from notes
            </button>
            <button
              className="primary-button"
              onClick={() => setEditing({ ...emptyItem })}
            >
              Add responsibility
            </button>
          </div>
        </header>
        {!data.preferences.enabled && (
          <div className="surface-panel mb-5 flex flex-wrap items-center justify-between gap-3 border-[#e6cb94] p-4">
            <p className="text-sm">
              Background monitoring is off. Enable it to check responsibilities
              while you are away.
            </p>
            <button
              className="secondary-button text-xs"
              onClick={() => setTab("Settings")}
            >
              Set up monitoring
            </button>
          </div>
        )}
        <label className="mb-5 block text-sm text-[#58635d]">Workspace
          <select aria-label="Workspace" className="field-input mt-2" value={tab} onChange={event => setTab(event.target.value)}>
            {views.map(value => <option key={value} value={value}>{value === 'Agent' ? 'Research & schedules' : value === 'Outbox' ? 'Drafts & approvals' : value}</option>)}
          </select>
        </label>
        {["Attention", "Responsibilities"].includes(tab) && (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <select
                aria-label="Filter by life area"
                className="rounded-md border-[#dce3dd] text-sm"
                value={domain}
                onChange={(event) => setDomain(event.target.value)}
              >
                {["all", ...domains].map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
              <label className="flex gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={showClosed}
                  onChange={(event) => setShowClosed(event.target.checked)}
                />
                Include completed
              </label>
            </div>
            {items.length ? (
              <div className="grid gap-4 xl:grid-cols-2">
                {items.map((item) => (
                  <ResponsibilityCard
                    key={item.id}
                    item={item}
                    timezone={data.timezone}
                    run={run}
                    busy={busy}
                    edit={setEditing}
                    openAgent={() => setTab("Agent")}
                    openPeople={() => setTab("People")}
                    openFinance={() => setTab("Finance")}
                    openVehicles={() => setTab("Vehicles")}
                    openRoutines={() => setTab("Routines")}
                    draft={(value) => {
                      setDraftItem(value);
                      setTab("Outbox");
                    }}
                  />
                ))}
              </div>
            ) : (
              <section className="surface-panel p-8">
                <h2 className="text-lg font-bold">
                  {data.responsibilities.length
                    ? "Nothing in this view needs attention"
                    : "Give Timelyzer something to look after"}
                </h2>
                <p className="mt-2 text-sm text-[#66716a]">
                  Add a responsibility or connect a source. Start with a pattern
                  below, then add your own details.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {templates.map(
                    ([title, area, kind, recurrence, lead_minutes]) => (
                      <button
                        key={title}
                        className="secondary-button text-xs"
                        onClick={() =>
                          setEditing({
                            ...emptyItem,
                            domain: area,
                            kind,
                            recurrence,
                            lead_minutes,
                          })
                        }
                      >
                        {title}
                      </button>
                    ),
                  )}
                </div>
              </section>
            )}
            <form
              className="surface-panel mt-5 space-y-3 p-5"
              onSubmit={(event) => {
                event.preventDefault();
                run(async () =>
                  setLinks(await assistantService.research(research, location)),
                );
              }}
            >
              <h2 className="font-bold">Research the next step</h2>
              <p className="text-xs text-[#66716a]">
                Find current courses, events, gifts, podcasts, routes, shops or
                weekend activities.
              </p>
              <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
                <Field title="What are you looking for?">
                  <input
                    required
                    className={inputClass}
                    value={research}
                    onChange={(event) => setResearch(event.target.value)}
                  />
                </Field>
                <Field title="Location (optional)">
                  <input
                    className={inputClass}
                    value={location}
                    onChange={(event) => setLocation(event.target.value)}
                  />
                </Field>
                <button disabled={busy} className="secondary-button self-end">
                  Find sources
                </button>
              </div>
              {links && (
                <div>
                  <p className="mb-2 text-xs text-[#66716a]">{links.message}</p>
                  <div className="flex flex-wrap gap-2">
                    {links.links.map((link) => (
                      <a
                        key={link.url}
                        className="secondary-button text-xs"
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {link.label} ↗
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </form>
          </>
        )}
        {tab === "Connections" && (
          <Connections
            connections={connections}
            sources={data.sources}
            run={run}
            busy={busy}
            extractSource={(value) => {
              setContent(value.slice(0, 30000));
              setExtractOpen(true);
            }}
          />
        )}
        {tab === "Agent" && (
          <AgentDesk
            run={run}
            busy={busy}
            openSettings={() => setTab("Settings")}
            openFinance={() => setTab("Finance")}
            editProposal={(proposal) =>
              setEditing({
                ...emptyItem,
                ...proposal.task,
                _agentProposalId: proposal.id,
              })
            }
          />
        )}
        {tab === "People" && (
          <PeopleDesk run={run} busy={busy} openAgent={() => setTab("Agent")} />
        )}
        {tab === "Trackers" && <TrackerSources />}
        {tab === "Mail" && <MailSources />}
        {tab === "Vehicles" && <VehicleDesk run={run} busy={busy} />}
        {tab === "Routines" && <RoutineDesk run={run} busy={busy} />}
        {tab === "Finance" && (
          <FinanceDesk
            run={run}
            busy={busy}
            sources={data.sources}
            openOutbox={() => setTab("Outbox")}
          />
        )}
        {tab === "Notifications" && <NotificationDesk />}
        {tab === "Settings" && (
          <Settings
            preferences={data.preferences}
            runtime={data.runtime}
            run={run}
            busy={busy}
          />
        )}
        {tab === "Outbox" && (
          <div className="space-y-4">
            <ActionForm
              key={draftItem?.id || "new"}
              initial={draftItem}
              run={run}
              busy={busy}
              onSaved={() => setDraftItem(null)}
            />
            {receiptAction.error && <p role="alert">This document update record could not be loaded. Check that you are signed in to the correct account.</p>}
            {actionKey && (selectedAction.error || !validActionKey) && <p role="alert">This Outbox record could not be loaded. Check that you are signed in to the correct account.</p>}
            {validActionKey && selectedAction.isPending && <p role="status">Loading the selected Outbox record…</p>}
            {(selectedAction.data || receiptAction.data ? [selectedAction.data || receiptAction.data, ...data.actions.filter(action => action.id !== (selectedAction.data || receiptAction.data).id)] : data.actions).map((action) => (
              <article className="surface-panel p-5" key={action.id} id={action.payload.document_reference?.receipt_key}>
                <div className="flex flex-wrap justify-between gap-2">
                  <h3 className="font-bold">{action.title}</h3>
                  <span className="status-pill">{label(action.status)}</span>
                </div>
                <p className="mt-2 text-xs text-[#66716a]">
                  {label(action.kind)} ·{" "}
                  {(action.payload.recipients || []).join(", ") ||
                    action.payload.target_id}
                </p>
                {action.payload.cc?.length > 0 && (
                  <p className="text-xs">Cc: {action.payload.cc.join(", ")}</p>
                )}
                {["docs_append", "notion_append"].includes(action.kind) && (
                  <DocumentActionReview action={action} busy={busy} run={run} />
                )}
                {action.payload.mail_reference && (
                  <Link
                    className="text-sm underline"
                    to={`/mail/threads/${action.payload.mail_reference.thread_id}`}
                  >
                    Open conversation and follow-up
                  </Link>
                )}
                {action.payload.meeting_reference && (
                  <Link className="text-sm underline" to={`/meetings/${action.payload.meeting_reference.event_id}`}>
                    Review meeting notes and follow-up history
                  </Link>
                )}
                {action.payload.tracker_reference && <Link className="mr-3 mt-2 inline-flex text-sm underline" to={`/trackers/${action.payload.tracker_reference.source_id}`}>
                  Review tracker row {action.payload.tracker_reference.external_id}
                </Link>}
                {action.kind === "email" &&
                  ["sending", "needs_review"].includes(action.status) && (
                    <button
                      disabled={busy}
                      className="secondary-button"
                      onClick={() =>
                        run(() => mailService.reconcile(action.id))
                      }
                    >
                      Check Gmail Sent result
                    </button>
                  )}
                {action.payload.start && (
                  <p className="mt-2 text-sm">
                    {displayTime(action.payload.start, data.timezone)} —{" "}
                    {displayTime(action.payload.end, data.timezone)}
                  </p>
                )}
                <p className="mt-4 whitespace-pre-wrap text-sm">
                  {action.payload.body}
                </p>
                {action.result?.message && (
                  <p role="status" className="mt-3 text-sm text-[#996617]">
                    {action.result.message}
                  </p>
                )}
                {action.status === "draft" && (
                  <div className="mt-4 flex gap-2">
                    <button
                      disabled={busy || (["docs_append", "notion_append"].includes(action.kind) && action.payload.document_reference?.receipt_version !== 1)}
                      className="primary-button"
                      onClick={() =>
                        run(() => assistantService.execute(action.id))
                      }
                    >
                      {action.kind === "calendar"
                        ? "Send invitations"
                        : action.kind === "email"
                          ? "Send email"
                          : "Append these notes"}
                    </button>
                    <button
                      disabled={busy}
                      className="secondary-button"
                      onClick={() =>
                        run(() => assistantService.cancel(action.id))
                      }
                    >
                      Cancel draft
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
        {editing && (
          <div
            className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Responsibility editor"
          >
            <div className="mx-auto my-6 max-w-2xl rounded-lg bg-[#f4f6f3] p-6">
              <h2 className="mb-4 text-lg font-bold">
                {editing.id ? "Edit responsibility" : "New responsibility"}
              </h2>
              <ResponsibilityForm
                initial={editing}
                busy={busy}
                onClose={() => setEditing(null)}
                onSave={(values) =>
                  run(async () => {
                    if (editing._agentProposalId)
                      await agentService.decide(editing._agentProposalId, {
                        action: "accept",
                        task: values,
                      });
                    else if (editing.id)
                      await assistantService.edit(editing.id, values);
                    else await assistantService.create(values);
                    if (editing._proposalIndex != null) {
                      setExtraction((current) =>
                        current
                          ? {
                              ...current,
                              items: current.items.filter(
                                (_, index) => index !== editing._proposalIndex,
                              ),
                            }
                          : current,
                      );
                    }
                    setEditing(null);
                  })
                }
              />
            </div>
          </div>
        )}
        {extractOpen && (
          <div
            className="fixed inset-0 z-40 overflow-y-auto bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Extract responsibilities"
          >
            <div className="mx-auto my-6 max-w-2xl space-y-4 rounded-lg bg-[#f4f6f3] p-6">
              <h2 className="text-lg font-bold">
                Turn notes into responsibilities
              </h2>
              <p className="text-sm text-[#66716a]">
                Paste meeting notes or life updates. AI proposes actions for
                review and flags missing dates. Nothing is saved until you
                review an item.
              </p>
              <textarea
                aria-label="Notes to extract"
                rows={7}
                maxLength={30000}
                className={inputClass}
                value={content}
                onChange={(event) => setContent(event.target.value)}
              />
              <div className="flex gap-2">
                <button
                  disabled={busy || !content.trim()}
                  className="primary-button"
                  onClick={() =>
                    run(async () =>
                      setExtraction(await assistantService.extract(content)),
                    )
                  }
                >
                  Extract actions
                </button>
                <button
                  className="secondary-button"
                  onClick={() => setExtractOpen(false)}
                >
                  Close
                </button>
              </div>
              {extraction?.questions.map((question, index) => (
                <p key={index} className="text-sm text-[#996617]">
                  {question}
                </p>
              ))}
              {extraction?.items.map((item, index) => (
                <div className="surface-panel p-4" key={index}>
                  <p className="font-bold">{item.title}</p>
                  <p className="mt-1 text-xs text-[#66716a]">
                    {item.domain} · {displayTime(item.due_at, data.timezone)}
                  </p>
                  <button
                    className="secondary-button mt-3 text-xs"
                    onClick={() => {
                      setEditing({
                        ...emptyItem,
                        ...item,
                        _proposalIndex: index,
                      });
                    }}
                  >
                    Review & save
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
